import { useMutation, useSuspenseQuery } from '@apollo/client'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Trans } from '@lingui/react/macro'

import { StepTransitions } from '@/components/ui/StepTransitions'
import { Step, usePmsSteps } from '../hooks/usePmsSteps'
import { LoadingComponent } from './LoadingComponent'
import { ProviderSelector } from './ProviderSelector'
import { FormComponent } from './FormComponent'
import { ConnectedView } from './ConnectedView'
import Button from '@/components/ui/Button'
import { LinkComponent } from './LinkComponent'
import { ButtonComponent } from './ButtonComponent'

function DynamicComponent(step: Step) {
  switch (step.type) {
    case 'form':
      return <FormComponent step={step} />
    case 'button':
      return <ButtonComponent step={step} />
    case 'link':
      return <LinkComponent step={step} />
    case 'loading':
      return <LoadingComponent step={step} />
    default:
      return null
  }
}

function PmsForms({ pms }: { pms: GraphQL.PmsFragmentFragment }) {
  const steps = usePmsSteps(
    pms.provider,
    pms.connectionData ? JSON.parse(pms.connectionData) : undefined
  )

  return (
    <StepTransitions
      steps={steps.map(step => ({
        Component: DynamicComponent(step),
        label: step.title,
        icon: step.icon,
        value: step.id
      }))}
      currentStep={pms.step}
    />
  )
}

export function PmsSteps() {
  const { data, error } = useSuspenseQuery(AdminAPI.pms.GET, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network'
  })

  const [remove, { loading }] = useMutation(AdminAPI.pms.REMOVE, {
    refetchQueries: [AdminAPI.pms.GET]
  })

  if (error || !data) {
    if (error?.message !== 'not_found') throw error

    return <ProviderSelector />
  }

  const pms = getFragment(SharedAPI.pms.PMS_FRAGMENT, data.getPms)

  if (pms.isConnected) return <ConnectedView pms={pms} />

  return (
    <div className="flex flex-col gap-1">
      <PmsForms pms={pms} />
      <div className="flex flex-row justify-start">
        <Button onClick={() => remove()} loading={loading}>
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </div>
  )
}
